import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import HeaderAndSidebar from 'components/templates/HeaderAndSidebar';
import { useState } from 'react';
import * as Types from 'types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

type Props = {
  isLoading: boolean;
  monthlyTotalAssets: Types.MonthlyTotalAsset[];
  onSubmit: (
    year: number,
    mointh: number,
    bank: number,
    wallet: number,
  ) => void;
  onClickMonthlyTotalAssetUpdate: (
    monthlyTotalAsset: Types.MonthlyTotalAsset,
  ) => void;
  onClickMonthlyTotalAssetDelete: (
    monthlyTotalAsset: Types.MonthlyTotalAsset,
  ) => void;
};

const MonthlyTotalAsset: React.FC<Props> = ({
  isLoading,
  monthlyTotalAssets,
  onSubmit,
  onClickMonthlyTotalAssetUpdate,
  onClickMonthlyTotalAssetDelete,
}) => {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const handleChangeYear = (event: SelectChangeEvent) => {
    setYear(event.target.value);
  };
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const handleChangeMonth = (event: SelectChangeEvent) => {
    setMonth(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const selectedYear = data.get('year');
    const selectedMonth = data.get('month');
    const inputBank = data.get('bank');
    const inputWallet = data.get('wallet');
    if (selectedYear && selectedMonth && inputBank && inputWallet) {
      onSubmit(
        parseInt(selectedYear.toString(), 10),
        parseInt(selectedMonth.toString(), 10),
        parseInt(inputBank.toString(), 10),
        parseInt(inputWallet.toString(), 10),
      );
    } else {
      console.log('failed');
    }
  };

  return (
    <HeaderAndSidebar pageTitle="総資産">
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <Stack spacing={3} maxWidth={400}>
              <Select
                label="年"
                name="year"
                value={year}
                onChange={handleChangeYear}
                autoWidth
              >
                {[2021, 2022, 2023, 2024, 2025, 2026].map((y, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MenuItem key={index} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
              <Select
                label="月"
                name="month"
                value={month}
                onChange={handleChangeMonth}
                autoWidth
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((y, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MenuItem key={index} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                margin="normal"
                required
                label="口座残高"
                name="bank"
              />
              <TextField
                margin="normal"
                required
                label="所持金"
                name="wallet"
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ maxWidth: '64px' }}
              >
                更新
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            総資産
          </Typography>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Table stickyHeader sx={{ maxHeight: 440 }}>
              <TableHead>
                <TableRow>
                  <TableCell>月</TableCell>
                  <TableCell>金額</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyTotalAssets.map((monthlyTotalAsset) => (
                  <TableRow key={monthlyTotalAsset.id}>
                    <TableCell>{`${monthlyTotalAsset.year}/${monthlyTotalAsset.month}`}</TableCell>
                    <TableCell>
                      {(
                        monthlyTotalAsset.wallet + monthlyTotalAsset.bank
                      ).toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        onClick={() =>
                          onClickMonthlyTotalAssetUpdate(monthlyTotalAsset)
                        }
                      >
                        編集
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() =>
                          onClickMonthlyTotalAssetDelete(monthlyTotalAsset)
                        }
                      >
                        削除
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Bar
              data={{
                labels: monthlyTotalAssets.map(
                  (asset) => `${asset.year}/${asset.month}`,
                ),
                datasets: [
                  {
                    data: monthlyTotalAssets.map(
                      (asset) => asset.wallet + asset.bank,
                    ),
                    backgroundColor: '#007bff',
                    borderColor: '#007bff',
                    label: '総資産',
                  },
                ],
              }}
            />
          )}
        </Paper>
      </Grid>
    </HeaderAndSidebar>
  );
};

export default MonthlyTotalAsset;
